import type React from "react";
import { BaseComposeInput } from "@/components/common/BaseComposeInput";
import type { BaseInputEditorVisibilityProps } from "@/components/common/BaseInputEditor";
import {
  COMMENT_MAX_FILE_COUNT,
  COMMENT_MAX_FILE_SIZE,
  COMMENT_MAX_LENGTH,
} from "@/constants";
import type { DraftStorageError } from "@/features/composer-drafts/draftRepository";
import type { ComposerDraftScope } from "@/features/composer-drafts/types";
import type { User } from "@/lib/humhub-api/types";

interface BaseCommentInputProps extends BaseInputEditorVisibilityProps {
  placeholder?: string;
  submitButtonLabel?: string;
  showFormatting?: boolean;
  onSubmit?: (content: string, files?: File[]) => Promise<void>;
  onCancel?: () => void;
  className?: string;
  disabled?: boolean;
  maxFileSize?: number;
  maxFileCount?: number;
  allowedFileTypes?: string[];
  mentionUsers?: User[];
  isLoadingMentionUsers?: boolean;
  hasMoreMentionUsers?: boolean;
  isFetchingMoreMentionUsers?: boolean;
  mentionLoadMoreError?: string | null;
  onLoadMoreMentionUsers?: () => Promise<User[]>;
  onMentionTrigger?: (query: string) => Promise<User[]>;
  mentionScopeKey?: string;
  autoFocus?: boolean;
  draftScope?: ComposerDraftScope;
  onDraftRestored?: () => void;
  onDraftRestorationSettled?: () => void;
  onDraftStorageError?: (error: DraftStorageError) => void;
  draftTargetUnavailable?: boolean;
}

export const BaseCommentInput: React.FC<BaseCommentInputProps> = ({
  placeholder,
  submitButtonLabel,
  showFormatting = true,
  onSubmit,
  onCancel,
  className = "",
  disabled,
  maxFileSize = COMMENT_MAX_FILE_SIZE,
  maxFileCount = COMMENT_MAX_FILE_COUNT,
  allowedFileTypes,
  mentionUsers = [],
  isLoadingMentionUsers = false,
  hasMoreMentionUsers = false,
  isFetchingMoreMentionUsers = false,
  mentionLoadMoreError,
  onLoadMoreMentionUsers,
  onMentionTrigger,
  mentionScopeKey,
  autoFocus = false,
  alwaysVisible = true, // Default to true as per user request for comments/replies
  draftScope,
  onDraftRestored,
  onDraftRestorationSettled,
  onDraftStorageError,
  draftTargetUnavailable,
}) => {
  return (
    <BaseComposeInput
      placeholder={placeholder}
      submitButtonLabel={submitButtonLabel}
      showFormatting={showFormatting}
      onSubmit={onSubmit}
      onCancel={onCancel}
      className={className}
      disabled={disabled}
      maxFileSize={maxFileSize}
      maxFileCount={maxFileCount}
      maxLength={COMMENT_MAX_LENGTH}
      allowedFileTypes={allowedFileTypes}
      onMentionTrigger={onMentionTrigger}
      mentionScopeKey={mentionScopeKey}
      mentionUsers={mentionUsers}
      isLoadingMentionUsers={isLoadingMentionUsers}
      hasMoreMentionUsers={hasMoreMentionUsers}
      isFetchingMoreMentionUsers={isFetchingMoreMentionUsers}
      mentionLoadMoreError={mentionLoadMoreError}
      onLoadMoreMentionUsers={onLoadMoreMentionUsers}
      autoFocus={autoFocus}
      alwaysVisible={alwaysVisible}
      context="post"
      draftScope={draftScope}
      onDraftRestored={onDraftRestored}
      onDraftRestorationSettled={onDraftRestorationSettled}
      onDraftStorageError={onDraftStorageError}
      draftTargetUnavailable={draftTargetUnavailable}
      containerClassNameOverride="rounded-2xl border-[1.5px] border-[#dae3e5] bg-white p-3 shadow-none focus-within:border-rw-main"
    />
  );
};
